class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    // Save the current scroll position
    const scrollY = window.scrollY;
    
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";


    // Restore the scroll position
    window.scrollTo(0, scrollY);

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => {
      oldNode.remove()
    }, 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  //console.log('focus:')
  //console.log(elementToFocus)
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  setTimeout(() => {
    elementToFocus.focus();
    if (
      elementToFocus.tagName === "INPUT" &&
      ["search", "text", "email", "url"].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      //console.log('setSelectionRange')
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }, 0);
}


// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  //allow inline autoplay to continue
  document.querySelectorAll("video:not(.video-media__video)").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

function setViewportHeight() {
  // Check if the width has changed
  if (window.innerWidth !== setViewportHeight.lastWidth) {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    setViewportHeight.lastWidth = window.innerWidth;
  }
}

// Call setViewportHeight() on page load
window.addEventListener('DOMContentLoaded', () => {
  setViewportHeight();
  setViewportHeight.lastWidth = window.innerWidth;
});

// Call setViewportHeight() when the width changes
window.addEventListener("resize", setViewportHeight);


// Include a device change listener for those pesky in-app browsers
// const mediaQuery = window.matchMedia("(min-width: 60em)");
// mediaQuery.addEventListener("change", setViewportHeight);
/**
 * Handles the load event for a lazy-loaded image, animating its transition
 * from the "lazy-loading" state to the "lazy-loaded" state.
 * @param {Event} event - The load event for the image.
 */
function handleOnLoad(event) {  
  const currentImage = event.currentTarget;
  // Add the "lazy-loading" and "lazy-loaded" classes to the image.
  currentImage.classList.add('lazy-loading');
  // Remove the "lazy-loading" class
  setTimeout(() => {
    currentImage?.classList?.add('lazy-loaded');
    currentImage?.classList?.remove('lazy-loading');
  }, 350);
}

/**
 * Initializes the animation of a lazy-loaded image, either by marking it
 * as "lazy-loaded" if it has already been loaded, or by adding a "load" 
 * event listener to animate the image when it finishes loading.
 * @param {HTMLImageElement} image - The image element to animate.
 */
function handleImage(image) {
  if (image.complete) {
    // If the image has already loaded, mark it as "lazy-loaded" immediately.
    image.classList.add('lazy-loading')
    setTimeout(() => {
      image.classList.add('lazy-loaded');
      image.classList.remove('lazy-loading');
    }, 350);
    image.removeEventListener('load', handleOnLoad, false);
  } else {
    // Otherwise, add a "load" event listener to animate the image when it loads.
    image.addEventListener('load', handleOnLoad, false);
  }
}

/**
 * Initializes the animation of all lazy-loaded images that are not already 
 * marked as "lazy-loaded" within the specified root element and its subtree.
 * @param {HTMLElement} rootEl - The root element to search for images.
 */
function initializeLazyImageAnimation(rootEl = document) {
  // Find all lazy-loaded images within the root element and its subtree,
  // and initialize their animation.
  rootEl.querySelectorAll('img[loading^="lazy"]:not(.lazy-loaded)').forEach((image) => {
    handleImage(image)
  });
}

// Create a MutationObserver to detect changes to the DOM and animate new images.
const mutationObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'childList') {
      mutation.addedNodes.forEach((node) => {
        // If a new image with the "lazy" attribute is added, initialize its animation.
        if (node.tagName === 'IMG' && node.getAttribute('loading') === 'lazy') {
          handleImage(node)
        }
        // If a new node is added that has children, recursively initialize its children.
        else if (node.firstChild !== null) {
          initializeLazyImageAnimation(node)
        }
      });
    }
  });
});

// Observe the entire body and its subtree for changes.
const config = { childList: true, subtree: true };
mutationObserver.observe(document.body, config);

// Initialize the animation of all existing lazy-loaded images on page load.
window.addEventListener('DOMContentLoaded', () => initializeLazyImageAnimation());
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.mainSummary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.mainDetailsToggle.querySelector('summary + *');
    this.hasSubMenu = this.dataset.submenu;

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    if (this.hasSubMenu) {
      this.querySelectorAll("summary").forEach((summary) =>
        summary.addEventListener("click", this.onSummaryClick.bind(this))
      );
    } else {
      this.mainSummary.addEventListener("click", this.onSummaryClick.bind(this))
    }
    
    this.querySelectorAll("button:not(.localization-selector, .reset__button)").forEach(
      (button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {

    console.log(event.code.toUpperCase())

    if (event.code.toUpperCase() !== "ESCAPE") return;

    console.log(event.target)
    const openDetailsElement = event.target.closest("details[open]");

    console.log(openDetailsElement)
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      //console.log(detailsElement)
      if (isOpen) event.preventDefault();
      //console.log(isOpen)
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 60em)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");

    //dont close filter submenus
    if(!this.mainDetailsToggle.querySelector("facet-filters-form")){
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        .querySelectorAll(".submenu-open")
        .forEach((submenu) => {
          submenu.classList.remove("submenu-open");
        });
    }
    

    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );

    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    //console.log('bye aria')
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    //console.log('animate close')
    //console.log(detailsElement)
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = this.header || document.querySelector(".section-header");
  }

  closeAllHeaderUnsubscriber = undefined;

  connectedCallback() {
    this.closeAllHeaderUnsubscriber = subscribe(PUB_SUB_EVENTS.closeAllHeader, (event) => {
      if (event.source === 'header-drawer' || !this.mainDetailsToggle.hasAttribute("open")) {
        return;
      }
      this.onCloseAllHeader(event);
    });
  }

  disconnectedCallback() {
    if (this.closeAllHeaderUnsubscriber) {
      this.closeAllHeaderUnsubscriber();
    }
  }

  onCloseAllHeader(event) {
    //console.log('close the header-drawer')
    //console.log(event)
    this.quickClose();
  }

  openMenuDrawer(summaryElement) {

    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom
      )}px`
    );
    this.header.classList.add("menu-open");

    publish(PUB_SUB_EVENTS.closeAllHeader, {source: 'header-drawer'});  

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  quickClose() {
    console.log('header-drawer quickClose')
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    //removeTrapFocus(elementToFocus);
    super.closeAnimation(this.mainDetailsToggle);
    this.mainSummary.setAttribute("aria-expanded", false);
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    
    // Find and cache relevant elements for future use
    this.header = document.querySelector('.header-wrapper');
    this.details = this.querySelector('details');
    this.summary = this.details.querySelector('summary');
    this.content = this.details.querySelector('summary').nextElementSibling;
    this.inner = this.content.querySelector('.mega-menu__inner');  

    // Add event listeners to relevant elements
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.details.addEventListener('focusout', this.onDetailsFocusOut.bind(this));
    
    // Detect if device supports pointer events (such as mouse or touch)
    const supportsPointer = window.matchMedia('(pointer:fine)').matches;

    // If device supports pointer events, add mouse event listeners
    if (supportsPointer) {
      //console.log('!supported')
      this.summary.addEventListener('mouseenter', this.onSummaryMouseEnter.bind(this));
    }
  }

  closeAllHeaderUnsubscriber = undefined;

  connectedCallback() {
    this.closeAllHeaderUnsubscriber = subscribe(PUB_SUB_EVENTS.closeAllHeader, (event) => {
      //console.log(event.source)
      if (event.source === 'header-menu' || !this.details.hasAttribute("open")) {
        return;
      }
      this.onCloseAllHeader();
    });
  }

  disconnectedCallback() {
    if (this.closeAllHeaderUnsubscriber) {
      this.closeAllHeaderUnsubscriber();
    }
  }

  onCloseAllHeader() {
    //console.log('header-menu recieved: closeHeaderMenu()')
    this.closeHeaderMenu();
  }

  // Handles the summary element being clicked
  onSummaryClick(event) {
    //console.log('onSummaryClick')
    const isOpen = this.details.hasAttribute("open");

    if (isOpen) {
      event.preventDefault();
    }

    isOpen
      ? this.closeHeaderMenu(event)
      : this.openHeaderMenu(this.summary);
  }

  // Handles the summary element being entered with a pointer (mouse or touch)
  onSummaryMouseEnter(event) {
    //console.log('onSummaryMouseEnter')
    const isOpen = this.details.setAttribute("open", true);

    this.header.addEventListener('mouseleave', this.onHeaderMouseLeave.bind(this));

    if (isOpen) {
      event.preventDefault();
    }

    isOpen
      ? this.closeHeaderMenu(event)
      : this.openHeaderMenu(this.summary);
  }

  // Handles the pointer leaving the header element
  onHeaderMouseLeave(event) {
    this.header.removeEventListener('mouseleave', this.onHeaderMouseLeave);
    
    if (event === undefined) return;

    this.details.classList.remove("header-menu-open");
    this.details.classList.remove("header-menu-opening");
    this.closeAnimation(this.details);
  }

  // Handles the details element losing focus
  onDetailsFocusOut() {
    //console.log('onDetailsFocusOut')
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // Opens the header menu and performs necessary animations
  openHeaderMenu(summaryElement) {
    //console.log('openHeaderMenu')

    // close other open header-menus
    this.closeOthers();

    // close other drawers/menus
    publish(PUB_SUB_EVENTS.closeAllHeader, {source: 'header-menu'});  

    setTimeout(() => {
      this.details.classList.add("header-menu-opening");
      this.revealContentsAnimation();
    });

    summaryElement.setAttribute("aria-expanded", true);
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  // Closes the header menu and performs necessary animations
  closeHeaderMenu(event) {
    //console.log('closeHeaderMenu')
    //if (event === undefined) return;

    this.details.classList.remove("header-menu-open");
    this.details.classList.remove("header-menu-opening");
    this.closeAnimation();
  }

  // Animates the closing of the header menu
  closeAnimation() {
    //console.log('closeAnimation')
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 150) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        //console.log('remove open')
        this.details.removeAttribute("open");
        //console.log('remove header-menu-active')
        this.header.classList.remove("header-menu-active");
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  // Animates the opening of of the header menu contents
  revealContentsAnimation() {
    //console.log('revealContentsAnimation')
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 150) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.details.classList.add("header-menu-open");
        this.header.classList.add("header-menu-active");
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  // Closes the details element
  close() {
    this.details.classList.remove("header-menu-open");
    setTimeout(() => {
      //console.log('close')
      this.details.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', false);
      this.details.classList.remove("header-menu-opening");
    });
  }

  // Closes currently open menus for when "panning" between menus
  closeOthers() {
    this.openHeaderMenuDetails = this.header.querySelectorAll('header-menu > details[open]')

    if(!this.openHeaderMenuDetails) return;
    
    this.openHeaderMenuDetails.forEach(openHeaderMenuDetail => {
      const openHeaderMenu = openHeaderMenuDetail.parentNode
      if (this !== openHeaderMenu) openHeaderMenu.close(openHeaderMenu);
    });
  }

}

customElements.define('header-menu', HeaderMenu);
class SearchDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  closeAllHeaderUnsubscriber = undefined;

  connectedCallback() {
    this.closeAllHeaderUnsubscriber = subscribe(PUB_SUB_EVENTS.closeAllHeader, (event) => {
      if (event.source === 'search-drawer' || !this.mainDetailsToggle.hasAttribute("open")) {
        return;
      }
      this.onCloseAllHeader(event);
    });
  }

  disconnectedCallback() {
    if (this.closeAllHeaderUnsubscriber) {
      this.closeAllHeaderUnsubscriber();
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    this.closeMenuDrawer(
      event,
      this.mainDetailsToggle.querySelector("summary")
    )
  }

  onCloseAllHeader(event) {
    //console.log('close the search drawer')
    //console.log(event)
    this.quickClose();
  }

  openMenuDrawer(summaryElement) {
    //console.log('search-drawer openMenuDrawer')
    this.header = this.header || document.querySelector(".section-header");

    
    this.setTemporaryInput();

    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom
      )}px`
    );
    this.header.classList.add("menu-open");

    publish(PUB_SUB_EVENTS.closeAllHeader, {source: 'search-drawer'});  


    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      this.revealContentsAnimation();
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    //trapFocus(this.mainDetailsToggle, summaryElement);

    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  setTemporaryInput(){
    // Create non-visible temporary input element and focus it
    this.inputHelper = document.createElement('input');
    this.inputHelper.style.position = 'absolute';
    this.inputHelper.style.width = '0';
    this.inputHelper.style.height = '0';
    this.inputHelper.style.margin = '0';
    this.inputHelper.style.padding = '0';
    this.inputHelper.style.border = '0';
    this.inputHelper.style.opacity = '0';
    this.appendChild(this.inputHelper);
    this.inputHelper.focus();
  }

  // Animates the opening of of the search drawer contents
  revealContentsAnimation() {
    //console.log('search-drawer revealContentsAnimation')
    //console.log('revealContentsAnimation')
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 250) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.mainDetailsToggle.classList.add("search-drawer-open");
        // trapFocus(
        //     this.mainDetailsToggle,
        //     this.mainDetailsToggle.querySelector('input:not([type="hidden"])')
        //   );
        setTimeout(() => {
          this.mainDetailsToggle.querySelector('input:not([type="hidden"])').focus()   
          this.removeChild(this.inputHelper);
        }, 250);
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  closeMenuDrawer(event, elementToFocus) {
    //console.log('search-drawer closeMenuDrawer')
    if (!elementToFocus) return;

    this.mainDetailsToggle.classList.remove("search-drawer-open");
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  quickClose() {
    this.mainDetailsToggle.classList.remove("search-drawer-open");
    this.mainDetailsToggle.classList.remove("menu-opening");
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    //removeTrapFocus(elementToFocus);
    super.closeAnimation(this.mainDetailsToggle);
    this.mainSummary.setAttribute("aria-expanded", false);
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    //console.log('search-drawer onResize')
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("search-drawer", SearchDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    //window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    //window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    //console.log('modal-opener mounted')

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      //console.log('click')
      //console.log(this)
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

if (!customElements.get("scroll-slider")) {
  // Define ScrollSlider as a global class first
  class ScrollSlider extends HTMLElement {
    constructor() {
      super();
      // set variables
      this.dragPos = { left: 0, x: 0 };
      this.isDragging = false;
      this.enableSliderDragging =
        this.hasAttribute("data-draggable") || false;

      // set references
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.currentPageElement = this.querySelector(
        ".slider-counter--current"
      );
      this.pageTotalElement = this.querySelector(".slider-counter--total");
      this.sliderControlWrapper = this.querySelector(".slider-buttons");
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.sliderIndexControl = this.querySelector(".slider-index-control");

      // if (!this.slider || !this.nextButton) return;

      if (!this.slider) return;

      // bind events
      this.bindEvents();

      // init Slide data
      this.initPages();

      // Resize observer for changes to slider behavior at different screen sizes
      const resizeObserver = new ResizeObserver((entries) =>
        this.initPages()
      );
      resizeObserver.observe(this.slider);

      // Setup mutation observer if data-mutation is "true"
      if (this.dataset.mutation === "true") {
        this.setupMutationObserver();
      }
    }

    // Bind event handlers
    bindEvents() {
      this.slider.addEventListener("scroll", this.update.bind(this));
      if (this.enableSliderDragging) {
        this.slider.addEventListener(
          "mousedown",
          this.onMouseDown.bind(this)
        );
      }

      this.prevButton?.addEventListener(
        "click",
        this.onButtonClick.bind(this)
      );
      this.nextButton?.addEventListener(
        "click",
        this.onButtonClick.bind(this)
      );

      if (this.sliderControlWrapper) {
        this.sliderControlLinksArray = Array.from(
          this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
        );
        this.sliderControlLinksArray.forEach((link) =>
          link.addEventListener("click", this.linkToSlide.bind(this))
        );
      }

      if (!this.sliderIndexControl) return;
      this.sliderIndexControlLinks = Array.from(
        this.sliderIndexControl.querySelectorAll(".slider-index")
      );
      this.sliderIndexControlLinks.forEach((link) =>
        link.addEventListener("click", this.linkToSlide.bind(this))
      );
    }

    setupMutationObserver() {
      const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (
            mutation.type === "childList" &&
            mutation.addedNodes.length > 0
          ) {
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]'); // Re-initialize slides
            this.initPages(); // Re-initialize pages when new slides are added
            this.updateControlLinks(); // Hide extra nodes in sliderControlLinksArray
          }
        }
      });

      // Observe changes to the slider's children (e.g., added slides)
      mutationObserver.observe(this.slider, { childList: true });
    }

    updateControlLinks() {
      // Ensure sliderControlLinksArray is updated
      if (!this.sliderControlLinksArray) return;

      // Hide extra control links
      const slidesCount = this.sliderItems.length;
      this.sliderControlLinksArray.forEach((link, index) => {
        if (slidesCount < 2) {
          link.style.display = "none"; // Hide all if count is les than the need for nav
        } else {
          if (index < slidesCount) {
            link.style.display = ""; // Show if within the slide count
          } else {
            link.style.display = "none"; // Hide if beyond the slide count
          }
        }
      });
    }

    // Initialize Page/Slide data
    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        (element) => element.clientWidth > 0
      );
      if (this.sliderItemsToShow.length < 2) return;
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset
      );
      this.totalPages =
        this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }

    // Update Slider Navigation Data on init and any scroll event
    update() {
      this.sliderIndexControlButtons = this.querySelectorAll(".slider-index");

      const previousPage = this.currentPage;
      this.currentPage =
        Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          })
        );
      }

      // if (
      //   this.isSlideVisible(this.sliderItemsToShow[0]) &&
      //   this.slider.scrollLeft < 64
      // ) {
      //   this.prevButton.setAttribute("disabled", "disabled");
      // } else {
      //   this.prevButton.removeAttribute("disabled");
      // }

      // if (
      //   this.isSlideVisible(
      //     this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      //   )
      // ) {
      //   this.nextButton.setAttribute("disabled", "disabled");
      // } else {
      //   this.nextButton.removeAttribute("disabled");
      // }

      // if (!this.sliderIndexControlButtons.length) return;

      // this.sliderIndexControlButtons.forEach((item, index) => {
      //   if (this.isSlideVisible(this.sliderItemsToShow[index])) {
      //     item.setAttribute("disabled", "disabled");
      //   } else {
      //     item.removeAttribute("disabled");
      //   }
      // });

      if (!this.sliderControlLinksArray) return;
      this.sliderControlLinksArray.forEach((item, index) => {
        if (this.isSlideVisible(this.sliderItemsToShow[index])) {
          item.setAttribute("disabled", "disabled");
          item.setAttribute("aria-current", true);
          item.classList.add("slider-counter__link--active");
        } else {
          item.removeAttribute("disabled");
          item.removeAttribute("aria-current", true);
          item.classList.remove("slider-counter__link--active");
        }
      });
    }

    // Check if a slide is visible
    isSlideVisible(element, offset = 0, wiggleRoom = 64) {
      if (!element) return;
      //console.log(`${this.slider.clientWidth} + ${this.slider.scrollLeft} - ${offset}`)
      const lastVisibleSlide = Math.round(
        this.slider.clientWidth + this.slider.scrollLeft - offset
      );
      //console.log(lastVisibleSlide)
      //console.log(`Is ${lastVisibleSlide} >= ${element.offsetLeft + element.clientWidth - wiggleRoom}  and also ${element.offsetLeft}  <= ${this.slider.scrollLeft - wiggleRoom}`)
      return (
        element.offsetLeft + element.clientWidth - wiggleRoom <=
          lastVisibleSlide &&
        element.offsetLeft >= this.slider.scrollLeft - wiggleRoom
      );
    }

    // Scroll to a slide
    setActiveSlide(mediaId) {
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      const slideArray = Array.from(this.sliderItems);
      const step = slideArray.indexOf(activeMedia);
      this.slideScrollPosition = step * this.sliderItemOffset;
      this.slider.scrollTo({
        behavior: "smooth",
        left: this.slideScrollPosition,
      });
    }

    setSlidePosition(position) {
      this.slider.scrollTo({
        left: position,
      });
    }

    linkToSlide(event) {
      event.preventDefault();
      const step = this.sliderControlLinksArray.indexOf(event.currentTarget);
      this.slideScrollPosition = step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }

    // Handle Previous/Next button click
    onButtonClick(event) {
      console.log('onButtonClick ScrollSlider');
      event.preventDefault();
      const step = event.currentTarget.dataset.step || 1;
      this.slideScrollPosition =
        event.currentTarget.name === "next"
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.slider.scrollTo({
        behavior: "smooth",
        left: this.slideScrollPosition,
      });
    }

    // Handle Mouse Down
    onMouseDown(event) {
      this.dragPos = {
        // The current scroll
        left: event.currentTarget.scrollLeft,
        // Get the current mouse position
        x: event.clientX,
      };

      this.isDragging = true;
      this.slider.classList.add("dragging");

      //add event listeners
      document.addEventListener("mousemove", this.onMouseMove.bind(this));
      document.addEventListener("mouseup", this.onMouseUp.bind(this));
    }

    // Handle Mouse Move
    onMouseMove(event) {
      if (!this.isDragging) return;

      // How far the mouse has been moved
      const dx = event.clientX - this.dragPos.x;

      // Scroll the element
      this.slider.scrollLeft = this.dragPos.left - dx;
    }

    // Handle Mouse Up
    onMouseUp(event) {
      this.isDragging = false;
      this.slider.classList.remove("dragging");

      // remove event listeners
      document.removeEventListener("mousemove", this.onMouseMove);
      document.removeEventListener("mouseup", this.onMouseUp);
    }
  }

  // Make ScrollSlider available globally
  window.ScrollSlider = ScrollSlider;

  // Register the custom element
  customElements.define("scroll-slider", ScrollSlider);
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset:not(.js-pseudo-variants) input:checked'
      )
    ).map(({ dataset }) => ({
      id: dataset.optionValueId,
      name: dataset.optionValueName,
    }));
  }
}

customElements.define('variant-selects', VariantSelects);
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          this.classList.add("product-recommendations--loaded");
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);
